// src/lib/managers/mail/MailViews.ts

import { EventBus } from "../../contexts";
import { logger, LOGS } from "../../utils";
import { getSinkPolicy } from "../../utils/logs/core";
import { CustomViewManager } from "../customView";
import { CustomViewType } from "../customView/types/customViews";
import { CategoryRepartition, ViewSummary } from "../customView/types/ui";
import { DataManager } from "../dataManager/DataManager";
import { MailFolder } from "../dataManager/graph/types/graph";
import {
    checkCounterInvariants,
    INVARIANT_TO_WARNING,
    type CounterSurfaces,
} from "./counters/checkCounterInvariants";
import { MailCopyRef } from "./domain/mailIdentity";
import { MailReader } from "./MailReader";
import { MailRecord } from "./MailRecord";
import { MailSync } from "./MailSync";
import {
    ACTIVE_SMART_LABELS,
    Categories,
    Mail,
    SmartLabel,
    SmartLabelInfos,
    SmartLabelSummary,
} from "./types/mail";
import { isQualifiedMail } from "./types/mail.api";
import { NavBarContent, NavItem, WelcomeMessages } from "./types/ui";
import { effectiveSmartLabel } from "./utils/effectiveSmartLabel";

const MAIL_FOLDERS: readonly MailFolder[] = [
    MailFolder.Inbox,
    MailFolder.SentItems,
    // MailFolder.Drafts,
    MailFolder.JunkEmail,
    MailFolder.Deleteditems,
    MailFolder.Archive,
];

export class MailViews {
    private eventBus: EventBus;

    private mailRecord: MailRecord;

    private mailSync: MailSync;

    private mailReader: MailReader;

    private dataManager: DataManager;

    private customViewManager: CustomViewManager;

    // Promise permettant d'awaiter une unique exécution de la fonction
    private navBarContentPromise: Promise<any> | null = null;

    private generalMailsFetchQueue: Promise<any> = Promise.resolve();

    private smartLabelMailsFetchQueue: Promise<any> = Promise.resolve();

    private customViewMailsFetchQueue: Promise<any> = Promise.resolve();

    private navBarContent: NavBarContent | null = null;

    // [AB#1710] Signatures (JSON) of the last emitted counter payloads. Used to skip redundant
    // emissions carrying identical values, which otherwise trigger needless dashboard redraws.
    private _lastNavigationUpdatedSignature: string | null = null;

    private _lastWelcomeMessagesSignature: string | null = null;

    private _lastDashboardViewsSignature: string | null = null;

    // [#1672 — instrumentation recette] Derniers payloads RÉELLEMENT émis par canal de compteur.
    // Servent à l'oracle d'invariants (`_checkCounterInvariantsAndWarn`) : on vérifie la cohérence
    // inter-surfaces sur ce que le FrontEnd a effectivement reçu (et non sur un re-calcul synchrone,
    // qui serait tautologique). Alimentés UNIQUEMENT quand un event est réellement émis (dédup AB#1710
    // respectée) → une surface « restée en arrière » (canal non ré-émis) apparaît comme divergente.
    private _lastEmittedWelcome: WelcomeMessages | null = null;

    private _lastEmittedNav: NavBarContent | null = null;

    private _lastEmittedViews: ViewSummary[] | null = null;

    // Cache dossiers (famille B). [#1672 — fix ④ Q-10] Le debounce couche 2 redondant a été retiré :
    // les recalculs sont déjà coalescés par le debounce couche 1 ('navigation-asking-for-update', 2 s).
    // On conserve UNIQUEMENT le cache TTL comme garde-fou anti-429 sur le chemin PULL.
    private mailFoldersCache: { data: NavItem[]; timestamp: number } | null =
        null;

    private readonly MAILFOLDERS_CACHE_TTL = 5000; // 5 sec

    constructor(
        eventBus: EventBus,
        mailRecord: MailRecord,
        mailSync: MailSync,
        mailReader: MailReader,
        dataManager: DataManager,
        customViewManager: CustomViewManager,
    ) {
        this.eventBus = eventBus;
        this.mailRecord = mailRecord;
        this.mailSync = mailSync;
        this.mailReader = mailReader;
        this.dataManager = dataManager;
        this.customViewManager = customViewManager;
    }

    public _cleanup(): void {
        this.navBarContentPromise = null;
        this.navBarContent = null;
        this.mailFoldersCache = null;
        this.generalMailsFetchQueue = Promise.resolve();
        this.smartLabelMailsFetchQueue = Promise.resolve();
        this.customViewMailsFetchQueue = Promise.resolve();
    }

    public _fetchFolderIds(): Promise<NavItem[]> {
        return this.dataManager.getGeneralNavBarContent([...MAIL_FOLDERS]);
    }

    public async _refreshCustomViewNavItems(): Promise<NavBarContent | null> {
        if (!this.navBarContent) return null;
        const customViewNavBarContent =
            await this._getCustomViewNavBarContent();
        if (!this.navBarContent) return null;
        this.navBarContent.customviewsNavItems = customViewNavBarContent;
        return this.navBarContent;
    }

    /**
     * (#1672 — fix ② Q-3) Réconciliation de la famille B (compteurs DOSSIERS = non-lus Graph).
     * Invalide le cache dossiers et redemande un recalcul → re-fetch `unreadItemCount` depuis Graph.
     * Appelée aux mêmes déclencheurs que la réconciliation SmartLabel (fin Phase 4 + détection de
     * suppression) pour que TOUS les compteurs convergent vers l'état réel.
     */
    public _reconcileFolderCounters(): void {
        this.mailFoldersCache = null;
        this.eventBus.emit("navigation-asking-for-update");
    }

    public async getGeneralMails(
        mailFolder: MailFolder,
        from: number,
        top: number,
    ): Promise<Mail[]> {
        logger.info(LOGS.MailManager, `getGeneralMails(${mailFolder})`);

        // Ajouter cette tâche à la queue FIFO
        const currentTask = this.generalMailsFetchQueue.then(async () => {
            try {
                const generalMails = await this.fetchGeneralMails(
                    mailFolder,
                    from,
                    top,
                );
                logger.info(
                    LOGS.MailManager,
                    `   => Returning ${generalMails.length} generalMails ${mailFolder} from ${from} top ${top}...`,
                );
                return generalMails;
            } catch (error) {
                logger.error(
                    LOGS.MailManager,
                    `   => Error fetching generalMails ${mailFolder} from ${from} top ${top} ! ${error}`,
                );
                throw new Error(
                    `   Error fetching generalMails ${mailFolder} from ${from} top ${top} : ` +
                        error,
                );
            }
        });

        // Mettre à jour la queue pour pointer vers la prochaine tâche
        // On utilise .catch() pour éviter que l'échec d'une tâche casse la chaîne
        this.generalMailsFetchQueue = currentTask.catch(() => {});

        // Retourner le résultat de cette tâche spécifique
        return currentTask;
    }

    private async fetchGeneralMails(
        mailFolder: MailFolder,
        from: number,
        top: number,
    ): Promise<Mail[]> {
        logger.info(
            [LOGS.MailManager, LOGS.AnalysisLifecycle],
            `fetchGeneralMails(${mailFolder})`,
        );
        this.mailSync._beginMailProcessing();
        try {
            // Listing du dossier (WS2 : MailCopyRef[] — id de copie Graph + parentFolderId du dossier listé).
            const copyRefs: MailCopyRef[] =
                await this.dataManager.fetchGeneralInternetMessageIds(
                    mailFolder,
                    top,
                    from,
                );

            // WS6 — deux chemins de fetch selon le type de vue (cf. 05 §2.3) :
            //   • Inbox = vue canonique (SmartLabel-derived) → fetch ENRICHI (analyse + DB), slot inbox.
            //   • Autres dossiers (Sent/Trash/Deleted/Archive/Junk) = vue Dossier → projection Graph DIRECTE
            //     (fetchMissingFolderCopiesIfAny, sans analyse/DB ; invariant : non-inbox = pures projections).
            const isInboxView = mailFolder === MailFolder.Inbox;

            if (isInboxView) {
                const internetMessageIds = copyRefs.map(
                    (ref) => ref.internetMessageId,
                );
                // WS7 : plus de réconciliation staleness ici — le fetch canonique cible le SLOT INBOX
                // (_findMissingCopies) ; une copie Sent seedée d'un self-sent n'empêche plus le fetch inbox.
                await this.mailSync.fetchMissingMailsIfAnyByInternetMessageIds(
                    internetMessageIds,
                    `MailManager.fetchGeneralMails(inbox)`,
                );
            } else {
                await this.mailSync.fetchMissingFolderCopiesIfAny(
                    copyRefs,
                    `MailManager.fetchGeneralMails(${mailFolder})`,
                );
            }

            // Get needed mails from Record — résolution SLOT-AWARE (dette WS3-(b) réglée).
            const generalMails: Mail[] = [];
            for (const ref of copyRefs) {
                // Inbox → copie canonique (slot inbox ; fallback mono-copie de _getCanonicalMail conservé).
                // Dossier → copie EXACTE du slot listé (= copiesForFolder résolu par slot via le ref).
                const mail = isInboxView
                    ? this.mailSync._getCanonicalMail(ref.internetMessageId)
                    : this.mailSync._getCopy(
                          ref.internetMessageId,
                          ref.parentFolderId,
                      );

                if (mail) {
                    // BUG-4 (option b) — vue Dossier (Corbeille incluse) = pure projection Graph, SANS effet de bord.
                    // L'ancien bloc de réconciliation Deleteditems (_removeCopyFromRecord du slot inbox + emit
                    // 'mail-deleted' PAR COPIE au simple affichage) a été retiré : le postulat « copie en Corbeille
                    // ⟹ copie inbox supprimée » est FAUX en multi-copies (self-sent dont seule la copie Sent est
                    // supprimée → le bloc évinçait la copie inbox VIVANTE) ; et l'émission par copie au chargement
                    // est coûteuse/bruyante (N events + fan-out nav). La détection des suppressions externes (Outlook)
                    // est assurée, mieux, par _verifyExistingMailsForView (inbox-aware via témoin, robuste, throttlé).
                    // Détail : Doc/identite-mail/INVESTIGATION_deleteditems-reconciliation.md
                    generalMails.push(mail);
                } else {
                    logger.warn(
                        [LOGS.MailManager],
                        `Copie introuvable dans le cache après récupération : ${ref.internetMessageId}@${ref.parentFolderId}`,
                    );
                }
            }

            return generalMails;
        } finally {
            this.mailSync._endMailProcessing();
        }
    }

    public async getSmartLabelMails(
        label: SmartLabel,
        from: number,
        top: number,
    ): Promise<Mail[]> {
        logger.info(LOGS.MailManager, `getSmartLabelMails(${label})`);

        // Ajouter cette tâche à la queue FIFO
        const currentTask = this.smartLabelMailsFetchQueue.then(async () => {
            try {
                const smartLabelMails = await this.fetchSmartLabelMails(
                    label,
                    from,
                    top,
                );
                logger.info(
                    LOGS.MailManager,
                    `   => Returning ${smartLabelMails.length} smartLabelMails from ${from} top ${top}...`,
                );
                return smartLabelMails;
            } catch (error) {
                logger.error(
                    LOGS.MailManager,
                    `   => Error fetching smartLabelMails from ${from} top ${top} ! ${error}`,
                );
                throw new Error(
                    `   Error fetching smartLabelMails from ${from} top ${top} : ` +
                        error,
                );
            }
        });

        // Mettre à jour la queue pour pointer vers la prochaine tâche
        // On utilise .catch() pour éviter que l'échec d'une tâche casse la chaîne
        this.smartLabelMailsFetchQueue = currentTask.catch(() => {});

        // Retourner le résultat de cette tâche spécifique
        return currentTask;
    }

    private async fetchSmartLabelMails(
        label: SmartLabel,
        from: number,
        top: number,
    ): Promise<Mail[]> {
        logger.info(
            [LOGS.MailManager, LOGS.AnalysisLifecycle],
            `fetchSmartLabelMails(${label}) (${from} top ${top})`,
        );

        // Nouvelle architecture : le label SmartLabel est passé directement comme customViewId.
        // _getInternetMessageIdsFromCustomView détecte les valeurs ACTIVE_SMART_LABELS et route
        // vers le store smartLabelEntries du CustomViewManager (pas de DB CustomView SmartLabel).
        const smartLabelMails: Mail[] = await this.getCustomViewMails(
            label,
            from,
            top,
        );

        logger.info(
            [LOGS.MailManager],
            `  => returning ${smartLabelMails.length} mails for smartLabel (${label})`,
        );

        return smartLabelMails;
    }

    public async getCustomViewMails(
        customViewId: string,
        from: number,
        top: number,
    ): Promise<Mail[]> {
        logger.info(
            LOGS.MailManager,
            `getCustomViewMails(${customViewId} from:${from} top:${top})`,
        );

        // Ajouter cette tâche à la queue FIFO
        const currentTask = this.customViewMailsFetchQueue.then(async () => {
            try {
                const customViewMails = await this.fetchCustomViewMails(
                    customViewId,
                    from,
                    top,
                );
                logger.info(
                    LOGS.MailManager,
                    `   => Returning ${customViewMails.length} customViewMails from ${from} top ${top}...`,
                );
                return customViewMails;
            } catch (error) {
                logger.error(
                    LOGS.MailManager,
                    `   => Error fetching customViewMails from ${from} top ${top} ! ${error}`,
                );
                throw new Error(
                    `   Error fetching customViewMails from ${from} top ${top} : ` +
                        error,
                );
            }
        });

        // Mettre à jour la queue pour pointer vers la prochaine tâche
        // On utilise .catch() pour éviter que l'échec d'une tâche casse la chaîne
        this.customViewMailsFetchQueue = currentTask.catch(() => {});

        // Retourner le résultat de cette tâche spécifique
        return currentTask;
    }

    private async fetchCustomViewMails(
        customViewId: string,
        from: number,
        top: number,
    ): Promise<Mail[]> {
        logger.info(
            LOGS.MailManager,
            `fetchCustomViewMails (${customViewId} from:${from} top:${top})`,
        );

        // Check input params
        if (!customViewId) {
            logger.error(LOGS.MailManager, "No custom view ID provided");
            throw new Error("No custom view ID provided");
        }

        try {
            // 1. IDs paginés de la vue (snapshot DB)
            const internetMessageIdsFromCustomView =
                await this.customViewManager._getInternetMessageIdsFromCustomView(
                    customViewId,
                    from,
                    top,
                );

            // 2. Type de vue — détermine la route de fetch ET les étapes conditionnelles ci-dessous
            const viewType: CustomViewType | null =
                this.customViewManager._getCustomViewType(customViewId);
            // Test POSITIF, et non `viewType !== Custom` : `_getCustomViewType` renvoie `null` pour une
            // vue SmartLabel (viewsRecordById ne contient que les vues Custom), donc tout id inconnu
            // passait pour un SmartLabel. Sans consequence tant que le flag ne pilotait qu'un re-fetch ;
            // il pilote desormais une exclusion d'affichage, ou un id inconnu produirait une liste VIDE
            // (aucun smartLabel ne peut egaler un uuid). Meme liste de reference que
            // `_getInternetMessageIdsFromCustomView`, qui route deja sur ACTIVE_SMART_LABELS.
            const isSmartLabelView: boolean = ACTIVE_SMART_LABELS.some(
                (label) => label === customViewId,
            );

            // 3. Fetch des mails absents du Record. Routage par type de vue : une vue
            //    SmartLabel est inbox-derived (invariant §3.2) ; une CustomView recrute dans Inbox ET
            //    Envoyes (cf. GraphManager.searchInternetMessageIdsFromViewFilter), et ses membres
            //    peuvent ensuite se retrouver ailleurs (corbeille) -> fetch sans restriction de dossier.
            if (isSmartLabelView) {
                await this.mailSync.fetchMissingMailsIfAnyByInternetMessageIds(
                    internetMessageIdsFromCustomView,
                    `MailManager.fetchCustomViewMails(${customViewId})`,
                );
            } else {
                await this.mailSync.fetchMissingMailsInAnyFolderIfAny(
                    internetMessageIdsFromCustomView,
                    `MailManager.fetchCustomViewMails(${customViewId})`,
                );
            }

            // 4. SmartLabel uniquement : re-fetch ciblé si des BaseMail inattendus sont en cache
            if (isSmartLabelView) {
                await this._refetchUnexpectedBaseMailsForSmartLabel(
                    customViewId,
                    internetMessageIdsFromCustomView,
                );
            }

            // 5. Résolution des objets Mail depuis le Record
            const customViewMails: Mail[] =
                internetMessageIdsFromCustomView.flatMap((id) => {
                    const mail = this.mailSync._getCanonicalMail(id);
                    if (!mail) {
                        logger.warn(
                            [LOGS.MailManager],
                            `Mail ${id} introuvable dans le cache après récupération`,
                        );
                    }
                    if (!mail) return [];

                    // Exclure les copies corbeille de TOUTE vue. AB#1900 : le gate `isSmartLabelView` ne
                    // tenait que parce que l'éviction message-entier empêchait une copie corbeille
                    // d'atteindre une CustomView — cf. `CustomViewManager._isMailMatchCustomView`.
                    if (
                        mail.parentFolderId ===
                        this.mailRecord.deleteditemsFolderId
                    ) {
                        logger.info(
                            [LOGS.MailManager],
                            `  Mail ${id} en corbeille — exclu de l'affichage de la vue (${customViewId})`,
                        );
                        return [];
                    }

                    // AB#1897 - le re-fetch de l'etape 4 a relu la ligne DB : un label encore divergent
                    // signifie que c'est le STORE qui est perime. Rendre ce mail l'afficherait avec le
                    // design d'un autre label (fond Focus sous << Pour info >>) ; on l'exclut, le
                    // prochain rafraichissement du store le remettra dans sa liste.
                    if (
                        isSmartLabelView &&
                        effectiveSmartLabel(mail) !== customViewId
                    ) {
                        logger.warn(
                            [LOGS.MailManager],
                            `  Mail ${id} labellise ${effectiveSmartLabel(mail)} - exclu de la liste ${customViewId}`,
                        );
                        return [];
                    }

                    return [mail];
                });

            // 6. Focus uniquement : exclut les invitations passées
            const focusFilteredMails = this._filterPastInvitationsForFocusView(
                customViewMails,
                viewType === CustomViewType.Focus,
            );

            // 7. Enrichissement PJ en arrière-plan (fire & forget — ne bloque pas le return)
            this.mailReader._startAttachmentEnrichmentWorkers(
                focusFilteredMails,
            );

            logger.info(
                [LOGS.TESTS, LOGS.MailManager],
                `Custom View (${customViewId}) Mails`,
                focusFilteredMails,
            );
            return focusFilteredMails;
        } catch (error) {
            logger.error(LOGS.MailManager, "Error fetching custom view:");
            throw new Error(error.message);
        }
    }

    /**
     * Detect & retry ciblé pour les BaseMail inattendus dans un contexte SmartLabel.
     * Dans une SmartLabel view, un mail est placé par l'analyse IA → le recevoir en BaseMail
     * indique un problème transitoire backend (cohérence DB, batch raté, etc.).
     * On supprime les IDs du Record pour forcer un re-fetch, puis on vérifie le résultat.
     * Scoped aux SmartLabels : dans une CustomView utilisateur, des BaseMails sont attendus.
     */
    private async _refetchUnexpectedBaseMailsForSmartLabel(
        customViewId: string,
        internetMessageIds: string[],
    ): Promise<void> {
        const inboxFolderIdForRefetch = this.mailRecord.inboxFolderId;
        const unexpectedBaseMailIds = internetMessageIds.filter((id) => {
            // AB#1900 — candidat = la copie INBOX, jamais une copie sœur : le repli mono-copie de
            // `_getCanonicalMail` rendrait une copie Sent/Archive, non qualifiée par nature, donc
            // reflaguée à chaque navigation (l'éviction large servait de cache négatif).
            // Rend aussi inutile l'ancien garde-fou corbeille : une copie corbeille n'est pas le slot inbox.
            const mail = inboxFolderIdForRefetch
                ? this.mailSync._getCopy(id, inboxFolderIdForRefetch)
                : undefined;
            if (mail === undefined) return false;
            const isNotQualified = !isQualifiedMail(mail);
            const isWrongSmartLabel =
                effectiveSmartLabel(mail) !== customViewId;

            return isNotQualified || isWrongSmartLabel;
        });

        if (unexpectedBaseMailIds.length === 0) return;

        logger.warn(
            [LOGS.MailManager, LOGS.AnalysisLifecycle],
            `_refetchUnexpectedBaseMailsForSmartLabel(${customViewId}) : ${unexpectedBaseMailIds.length} mail(s) non qualifie(s) ou mal labellise(s) — refetch ciblé.`,
        );
        logger.info(
            [LOGS.MailManager],
            `Mails perimes pour la vue SmartLabel`,
            unexpectedBaseMailIds,
        );

        // Retrait du slot INBOX pour forcer son re-fetch (`_findMissingCopies` est slot-aware).
        // AB#1900 : on ne touche plus aux copies sœurs, que le re-fetch ne reciblait jamais.
        unexpectedBaseMailIds.forEach((id) => {
            if (inboxFolderIdForRefetch) {
                this.mailSync._removeCopyFromRecord(
                    id,
                    inboxFolderIdForRefetch,
                );
            }
        });

        // forceMailAnalysis=false : on veut les données DB existantes, pas déclencher une nouvelle analyse
        await this.mailSync.fetchMissingMailsIfAnyByInternetMessageIds(
            unexpectedBaseMailIds,
            `MailManager._refetchUnexpectedBaseMailsForSmartLabel(${customViewId})`,
            false,
        );

        const stillBaseMailIds = unexpectedBaseMailIds.filter((id) => {
            const mail = this.mailSync._getCanonicalMail(id);
            if (mail === undefined) return false;
            const isNotQualified = !isQualifiedMail(mail);
            const isWrongSmartLabel =
                effectiveSmartLabel(mail) !== customViewId;

            return isNotQualified || isWrongSmartLabel;
        });

        if (stillBaseMailIds.length > 0) {
            logger.error(
                [LOGS.MailManager],
                `_refetchUnexpectedBaseMailsForSmartLabel(${customViewId}) : ${stillBaseMailIds.length} mail(s) encore non qualifie(s) ou mal labellise(s) apres refetch - exclus de la liste.`,
            );
            logger.info(
                [LOGS.MailManager],
                `Mails perimes persistants apres refetch`,
                stillBaseMailIds,
            );
        } else {
            logger.info(
                [LOGS.MailManager],
                `_refetchUnexpectedBaseMailsForSmartLabel(${customViewId}) : refetch reussi - tous les mails sont qualifies et bien labellises.`,
            );
        }
    }

    public async getDashboardViewsSummary(): Promise<ViewSummary[]> {
        logger.info(LOGS.MailManager, "getDashboardViewsSummary()");

        // Get NavBar Content
        const navBarContent = await this._calculateAllMailCounters();

        // Create the dashboard view Summary
        const dashboardViewSummaryCreated =
            this._buildAndEmitDashboardViewSummariesFromNavBarContent(
                navBarContent,
            );

        // Update welcome message
        this._buildAndEmitWelcomeMessagesFromNavBarContent(navBarContent);

        // [#1672] contrôle post fan-out (chemin PULL Dashboard)
        this._checkCounterInvariantsAndWarn("pull:getDashboardViewsSummary");

        return dashboardViewSummaryCreated;
    }

    /**
     * Creates a summary table of favorite views for the dashboard.
     * Automatically includes the "Focus" view and all custom views marked as favorites.
     *
     * @returns {Promise<ViewSummary[]>} Table of view summaries with email counters and breakdown by category.
     */
    private _buildAndEmitDashboardViewSummariesFromNavBarContent(
        navBarContent: NavBarContent,
    ): ViewSummary[] {
        logger.info(
            [LOGS.MailManager],
            `log _buildAndEmitDashboardViewSummariesFromNavBarContent()`,
        );

        let dashboardViewSummary: ViewSummary[] = [];

        try {
            // [#1672] `categoryRepartition` (répartition par catégorie IA du Focus) part d'ici vers le FrontEnd
            // via l'event 'dashboard-favorite-views-updated' → il alimente la BARRE SEGMENTÉE de la carte Focus
            // (`EmailProgressBar`). NE PAS confondre avec le WIDGET À BULLES du dashboard, qui est une répartition
            // par SmartLabel (bulle ∝ `itemCount`) et n'utilise pas `categoryRepartition`.
            dashboardViewSummary.push({
                viewId: SmartLabel.Focus,
                iconColor: "???",
                icon: "",
                title: "Focus",
                mailCount:
                    navBarContent.smartlabelNavItems.find(
                        (item) => item.id === SmartLabel.Focus,
                    )?.itemCount || 0,
                categoryRepartition:
                    navBarContent.smartlabelNavItems.find(
                        (item) => item.id === SmartLabel.Focus,
                    )?.categoryRepartition || [],
            });

            navBarContent.customviewsNavItems
                .filter((customView) => customView.isFavorite)
                .forEach((favoriteCustomView) => {
                    dashboardViewSummary.push({
                        viewId: favoriteCustomView.id,
                        iconColor: "???",
                        icon: favoriteCustomView.menuInfo.icon,
                        title: favoriteCustomView.menuInfo.title,
                        mailCount: favoriteCustomView.itemCount || -1,
                        categoryRepartition:
                            favoriteCustomView.categoryRepartition || [],
                    });
                });

            logger.info(
                [LOGS.MailManager],
                "Dashboard Views Summary",
                dashboardViewSummary,
            );

            // [AB#1710] Skip redundant emit when the favorite-view summaries are unchanged.
            const viewsSignature = JSON.stringify(dashboardViewSummary);
            if (viewsSignature !== this._lastDashboardViewsSignature) {
                this._lastDashboardViewsSignature = viewsSignature;
                this._lastEmittedViews = dashboardViewSummary; // [#1672] snapshot datacards réellement émis
                this.eventBus.emit(
                    "dashboard-favorite-views-updated",
                    dashboardViewSummary,
                );
            }

            return dashboardViewSummary;
        } catch (error) {
            logger.error(
                [LOGS.MailManager],
                `Error getting _buildAndEmitDashboardViewSummariesFromNavBarContent : ${error}`,
            );
            return dashboardViewSummary;
        }
    }

    public getWelcomeMessages(): WelcomeMessages {
        if (this._lastEmittedWelcome) {
            return this._lastEmittedWelcome;
        }

        if (this.navBarContent) {
            return this._buildWelcomeMessagesPayload(this.navBarContent);
        }

        // Aucun calcul navbar encore effectué : état nominal pré-init (pas une erreur) — compteurs à 0,
        // corrigés par le premier fan-out (event) ou le refetch au montage côté FE.
        return { newMailsCount: 0, importantMailsCount: 0, savedCO2Kg: 0 };
    }

    /**
     * [AB#1710] Projection navbar → welcome, source UNIQUE partagée par le PUSH
     * (`_buildAndEmitWelcomeMessagesFromNavBarContent`) et le PULL (`getWelcomeMessages`).
     */
    private _buildWelcomeMessagesPayload(
        navBarContent: NavBarContent,
    ): WelcomeMessages {
        return {
            newMailsCount:
                navBarContent.generalNavItems?.find(
                    (navitem) => navitem.id == MailFolder.Inbox,
                )?.itemCount || 0,
            importantMailsCount:
                navBarContent.smartlabelNavItems?.find(
                    (navitem) => navitem.id == SmartLabel.Focus,
                )?.itemCount || 0,
            savedCO2Kg: 0,
        };
    }

    /**
     * Builds welcome messages from navigation bar content and emits an event.
     *
     * @param navBarContent - The navigation bar content containing mail counts
     * @returns The constructed welcome messages object with mail counts and saved CO2
     */
    private _buildAndEmitWelcomeMessagesFromNavBarContent(
        navBarContent: NavBarContent,
    ): WelcomeMessages {
        const welcomeMessage: WelcomeMessages =
            this._buildWelcomeMessagesPayload(navBarContent);

        logger.info(
            [LOGS.MailManager],
            `_buildAndEmitWelcomeMessagesFromNavBarContent - welcome message updated`,
            welcomeMessage,
        );

        // [AB#1710] Skip redundant emit: only notify when the welcome counts actually changed.
        const welcomeSignature = JSON.stringify(welcomeMessage);
        if (welcomeSignature !== this._lastWelcomeMessagesSignature) {
            this._lastWelcomeMessagesSignature = welcomeSignature;
            this._lastEmittedWelcome = welcomeMessage; // [#1672] snapshot welcome réellement émis — relu par le PULL getWelcomeMessages (AB#1710)
            this.eventBus.emit(
                "dashboard-welcome-messages-updated",
                welcomeMessage,
            );
        }

        return welcomeMessage;
    }

    public getDashboardTips(): string {
        logger.info(LOGS.MailManager, "getDashboardTips()");

        //  1. Laissez MailVista trier vos mails en un coup d'œil
        // MAILVISTA trie automatiquement par priorité : MailVista analyse l’expéditeur, l’objet et le contexte pour identifier les mails urgents, nécessitant des réponses ou promotionnels.

        // 2. Utilisez les résumés intelligents
        // Mailvista “Résume ” découvrez en un clin d'œil une synthèse claire d’un long email ou d’un fil de discussion. Idéal pour gagner du temps sur les mails professionnels ou les comptes rendus.

        // 3. Générez des réponses en un clic
        // Sélectionnez “Répondre avec MailVista” pour recevoir une proposition de réponse adaptée au ton et au contexte de l’email. Vous pouvez la personnaliser avant envoi.

        //  4. Planifiez vos To Do automatiquement
        // MailVista tient compte de votre contexte pour ajouter des To-Do à partir du contenu d’un mail, de votre planning, une date de réunion ou une deadline. Utilisez le widget “To Do”.

        // 5. Posez des questions à MailVista
        // Tapez une question en langage naturel du type “Quels mails reçus la semaine dernière contiennent des pièces jointes ?” — MailVista saura y répondre rapidement.

        logger.warn(
            LOGS.TESTS,
            "getDashboardTips() - This is a fake tip, should be replaced by real AI tips",
        );
        // TODO - Implement real Tips List
        // TODO - Implement traduction feature to rpovide translated strings to Frontend
        return "MAILVISTA trie automatiquement par priorité : MailVista analyse l'expéditeur, l'objet et le contexte pour identifier les mails urgents, nécessitant des réponses ou promotionnels.";
    }

    public async getNavBarContent(): Promise<NavBarContent> {
        logger.info(LOGS.MailManager, "getNavBarContent()");

        if (this.navBarContent && this.navBarContent !== null) {
            return this.navBarContent;
        }

        // Si une requête est déjà en cours, attendez son résultat
        if (this.navBarContentPromise) {
            logger.info(
                LOGS.MailManager,
                "   => Waiting for ongoing navBarContent fetch ...",
            );
            return this.navBarContentPromise;
        }

        logger.info(LOGS.MailManager, "   => Fetching  navBarContent");
        this.navBarContentPromise = this._fetchNavBarContent();

        try {
            const navBarContent = await this.navBarContentPromise;

            logger.info(LOGS.MailManager, `   => Returning navBarContent`);
            return navBarContent;
        } catch (error) {
            logger.error(
                LOGS.MailManager,
                `   => Error fetching navBarContent ! ${error}`,
            );
            throw new Error("Error fetching navBarContent : " + error);
        } finally {
            this.navBarContentPromise = null;
        }
    }

    /**
     * Fetches and aggregates navigation bar content from multiple sources.
     *
     * Retrieves general navigation items, smart label items, and custom view items,
     * then combines them into a single NavBarContent object. Updates the internal
     * navBarContent property and emits a 'navigation-updated' event with the fetched data.
     *
     * @returns Promise<NavBarContent>
     */
    private async _fetchNavBarContent(): Promise<NavBarContent> {
        logger.info([LOGS.MailManager], `log fetchNavBarContent()`);

        const navBarContentData = await this._calculateAllMailCounters();
        this.navBarContent = navBarContentData;

        logger.info(
            [LOGS.MailManager],
            `Navbar Content fetched `,
            navBarContentData,
        );

        // [#1672 — fix ① fan-out atomique] Le chemin PULL doit alimenter LES MÊMES surfaces que le chemin
        // PUSH (`_recalculateCountersAndSendEvent`), depuis le MÊME `navBarContent`. Historiquement il n'émettait
        // que welcome + navigation, laissant la DataCard Focus (`dashboard-favorite-views-updated`) sur son
        // ancienne valeur → divergence INV-1 (doc 10, Méca-1). On émet désormais les 3 canaux ensemble.
        try {
            this._buildAndEmitWelcomeMessagesFromNavBarContent(
                navBarContentData,
            );
            this._buildAndEmitDashboardViewSummariesFromNavBarContent(
                navBarContentData,
            );
        } catch (error) {
            logger.error(
                LOGS.MailManager,
                `Error trying to build dashboard-welcome-messages-updated event`,
            );
            if (error instanceof Error) {
                logger.error(LOGS.MailManager, error.message, error);
            } else {
                logger.error(LOGS.MailManager, String(error));
            }
        }

        this._emitNavigationUpdatedIfChanged(navBarContentData);

        this._checkCounterInvariantsAndWarn("pull:_fetchNavBarContent"); // [#1672] contrôle post fan-out

        return navBarContentData;
    }

    /**
     * Calculates and caches all mail counters for the navigation bar.
     *
     * @returns The complete navigation bar content with updated mail counts
     */
    private async _calculateAllMailCounters(): Promise<NavBarContent> {
        // Récupère toutes les données de la navbar à jour
        const navBarContentData = {
            generalNavItems: await this._getGeneralNavBarContent(), // Fetch Graph folders
            smartlabelNavItems: await this._getSmartLabelNavBarContent(), // Retrieve length of mail from Record
            customviewsNavItems: await this._getCustomViewNavBarContent(), // Retrieve length of mail from Record
        };

        // Stock en cache la navbar à jour
        this.navBarContent = navBarContentData;

        return navBarContentData;
    }

    /**
     * Recalculates all mail counters, updates dashboard data, and emits navigation update event.
     */
    public async _recalculateCountersAndSendEvent() {
        // [#1672 — fix ③ Q-10/Q-3] Un recalcul PUSH signale un changement d'état connu (mutation
        // in-app, réconciliation, analyse…). On invalide le cache dossiers (famille B) pour que les
        // non-lus soient RE-FETCHés depuis Graph au lieu de servir une valeur périmée pendant la
        // fenêtre de TTL (B-01/B-05). Le cache TTL reste actif pour le chemin PULL (montage FE) et
        // comme garde-fou 429. Les recalculs PUSH sont bornés par le debounce couche 1 (2 s).
        this.mailFoldersCache = null;

        // Récupère tous les compteurs pour la navigation
        const navBarContentData = await this._calculateAllMailCounters();

        // Update welcome message
        this._buildAndEmitWelcomeMessagesFromNavBarContent(navBarContentData);

        // Update Dashboard views
        this._buildAndEmitDashboardViewSummariesFromNavBarContent(
            navBarContentData,
        );

        // Emit event for UI
        this._emitNavigationUpdatedIfChanged(navBarContentData);

        // [#1672] contrôle post fan-out (chemin PUSH : mutation in-app / réconciliation / analyse)
        this._checkCounterInvariantsAndWarn(
            "push:_recalculateCountersAndSendEvent",
        );
    }

    /**
     * [AB#1710] Emits 'navigation-updated' only when the payload actually changed.
     * Several code paths recompute and re-emit the navigation content with identical
     * counts; skipping byte-identical payloads avoids redundant UI redraws (the bubble
     * chart "juggling"). New subscribers still get the current state via getNavBarContent().
     */
    public _emitNavigationUpdatedIfChanged(navBarContent: NavBarContent): void {
        const signature = JSON.stringify(navBarContent);
        if (signature === this._lastNavigationUpdatedSignature) {
            return;
        }
        this._lastNavigationUpdatedSignature = signature;
        this._lastEmittedNav = navBarContent; // [#1672] snapshot du dernier payload nav réellement émis
        this.eventBus.emit("navigation-updated", navBarContent);
    }

    /**
     * [#1672 — instrumentation recette manuelle] Projette les DERNIERS payloads réellement émis
     * (welcome / navigation / datacards) en `CounterSurfaces`. Jumeau EXACT du `snapshotSurfaces`
     * de test (`__tests__/counters-1672/harness.ts`) : mêmes chemins d'extraction, pour que l'oracle
     * évalue en prod ce qu'il évalue en test. Une surface `undefined` = canal jamais émis (resté vide
     * / en arrière côté FE) → l'oracle le signale comme fan-out partiel (INV-1/INV-5).
     */
    private _buildCounterSurfacesFromLastEmitted(): CounterSurfaces {
        const welcome = this._lastEmittedWelcome;
        const nav = this._lastEmittedNav;
        const views = this._lastEmittedViews;

        const navSmartLabels: Partial<Record<SmartLabel, number>> = {};
        const focusNavItem = nav?.smartlabelNavItems.find(
            (i) => i.id === SmartLabel.Focus,
        );
        if (nav) {
            for (const item of nav.smartlabelNavItems) {
                navSmartLabels[item.id as SmartLabel] = item.itemCount;
            }
        }

        return {
            welcomeX: welcome?.newMailsCount,
            welcomeY: welcome?.importantMailsCount,
            navInbox: nav?.generalNavItems.find(
                (i) => i.id === MailFolder.Inbox,
            )?.itemCount,
            navFocus: focusNavItem?.itemCount,
            dataCardFocus: views?.find((v) => v.viewId === SmartLabel.Focus)
                ?.mailCount,
            navSmartLabels,
            focusRepartition: focusNavItem?.categoryRepartition?.map((r) => ({
                categoryId: r.categoryId,
                percentage: r.percentage,
            })),
        };
    }

    /**
     * [#1672 — instrumentation recette manuelle] Évalue l'oracle d'invariants sur les surfaces
     * réellement émises + le store SmartLabel (exclusivité), et émet un `logger.warn(LOGS.Counters1672, …)`
     * par violation (préfixé du code `W-*` de `12_RECETTE`). Un W-* affiché = invariant violé = KO.
     *
     * ENTIÈREMENT gated par la catégorie `LOGS.Counters1672` du sink console (défaut off, activable via
     * `FloatingLogSettings`) : zéro overhead et zéro effet de bord en fonctionnement normal.
     * Best-effort : ne throw jamais (une erreur d'instrumentation ne doit pas perturber les compteurs).
     *
     * @param context - étiquette du déclencheur (chemin de fan-out) pour tracer d'où vient la mesure.
     */
    private _checkCounterInvariantsAndWarn(context: string): void {
        if (
            !(getSinkPolicy("console")?.categories ?? []).includes(
                LOGS.Counters1672,
            )
        ) {
            return;
        }
        try {
            const surfaces = this._buildCounterSurfacesFromLastEmitted();

            // Store SmartLabel pour INV-4 (exclusivité). On ne récupère que les ids (l'oracle n'a
            // besoin que de `id` par label) → coût négligeable, aucune requête réseau.
            const store: Partial<Record<SmartLabel, { id: string }[]>> = {};
            for (const label of ACTIVE_SMART_LABELS) {
                store[label] = this.customViewManager
                    ._getSmartLabelInternetMessageIds(label)
                    .map((id) => ({ id }));
            }

            const violations = checkCounterInvariants(surfaces, { store });

            if (violations.length === 0) {
                logger.info(
                    LOGS.Counters1672,
                    `[#1672][${context}] invariants OK | welcomeX=${surfaces.welcomeX} welcomeY=${surfaces.welcomeY} ` +
                        `navInbox=${surfaces.navInbox} navFocus=${surfaces.navFocus} dataCardFocus=${surfaces.dataCardFocus}`,
                );
                return;
            }

            for (const v of violations) {
                const wCode = INVARIANT_TO_WARNING[v.code] ?? v.code;
                logger.warn(
                    LOGS.Counters1672,
                    `[#1672][${context}] ${wCode} (${v.code}) — ${v.message}`,
                );
            }
        } catch (error) {
            logger.warn(
                LOGS.Counters1672,
                `[#1672][${context}] échec du contrôle d'invariants (ignoré) : ${error}`,
            );
        }
    }

    /**
     * Retrieves navigation items for all standard mail folders
     * This function makes parallel calls to DataManager for each folder type
     *
     * @returns {Promise<NavItem[]>} An array of navigation items for all folders
     */
    private async _getGeneralNavBarContent(): Promise<NavItem[]> {
        const now = Date.now();

        // Check cache first (garde-fou anti-429 sur le chemin PULL ; invalidé par les recalculs PUSH).
        if (
            this.mailFoldersCache &&
            now - this.mailFoldersCache.timestamp < this.MAILFOLDERS_CACHE_TTL
        ) {
            logger.info([LOGS.MailManager], `Using cached navbar data`);
            // [#1672 — L-N3] Trace du compteur DOSSIER servi depuis le CACHE (diagnostic B-01/B-05/B-02 :
            // un compteur dossier périmé après suppression externe vient d'un cache non invalidé).
            this._logFolderCounter(
                "cache",
                this.mailFoldersCache.data,
                now - this.mailFoldersCache.timestamp,
            );
            return this.mailFoldersCache.data;
        }

        // [#1672 — fix ④ Q-10] Plus de debounce couche 2 : la coalescence est assurée par le debounce
        // couche 1 ('navigation-asking-for-update'). On fetch directement (le cache TTL borne la charge).
        return this.executeNavBarFetch();
    }

    /**
     * [#1672 — L-N3] Loggue le compteur non-lus du dossier Inbox (famille B) et sa provenance
     * (`cache` vs `graph`). Sert à trancher, après une suppression EXTERNE, si un compteur dossier
     * périmé vient d'un cache non invalidé (KO SDK) ou d'un `unreadItemCount` Graph encore en retard
     * (lag eventually-consistent, toléré). Gated par `LOGS.Counters1672`.
     */
    private _logFolderCounter(
        source: "cache" | "graph",
        items: NavItem[],
        cacheAgeMs?: number,
    ): void {
        if (
            !(getSinkPolicy("console")?.categories ?? []).includes(
                LOGS.Counters1672,
            )
        ) {
            return;
        }
        const inboxUnread = items.find(
            (i) => i.id === MailFolder.Inbox,
        )?.itemCount;
        const ageSuffix = source === "cache" ? ` cacheAgeMs=${cacheAgeMs}` : "";
        logger.info(
            LOGS.Counters1672,
            `[#1672][L-N3] folderCounter source=${source} inboxUnread=${inboxUnread}${ageSuffix}`,
        );
    }

    /**
     * Retrieves navigation items for all standard mail folders
     * This function makes parallel calls to DataManager for each folder type
     *
     * @returns {Promise<NavItem[]>} An array of navigation items for all folders
     */
    private async executeNavBarFetch(): Promise<NavItem[]> {
        try {
            const result = await this.dataManager.getGeneralNavBarContent([
                ...MAIL_FOLDERS,
            ]);

            // [#1672 — L-N3] Compteur dossier FRAÎCHEMENT récupéré depuis Graph (source de vérité non-lus).
            this._logFolderCounter("graph", result);

            // Update cache
            this.mailFoldersCache = {
                data: result,
                timestamp: Date.now(),
            };

            return result;
        } catch (error) {
            logger.error(
                LOGS.MailManager,
                `Error fetching general nav bar content! ${error}`,
                error,
            );
            return [];
        }
    }

    /**
     * Retrieves navigation items for all smart label folders with their respective counts
     * Data should be retrieved directly from customViewManager Record
     *
     * @returns {Promise<NavItem[]>} An array of navigation items for all smart label folders
     */
    private async _getSmartLabelNavBarContent(): Promise<NavItem[]> {
        let smartLabelNavItems: NavItem[] = [];

        try {
            const [
                focusSummary,
                pourInfoSummary,
                newsletterSummary,
                // suspectSummary,
                autreSummary,
                traitesSummary,
            ] = await Promise.all([
                this._getSmartLabelSummary(SmartLabel.Focus),
                this._getSmartLabelSummary(SmartLabel.PourInfo),
                this._getSmartLabelSummary(SmartLabel.Newsletter),
                // this.getSmartLabelSummary(SmartLabel.Suspect),
                this._getSmartLabelSummary(SmartLabel.Autre),
                this._getSmartLabelSummary(SmartLabel.Traites),
            ]);

            // Create NavItems with count information
            smartLabelNavItems = [
                {
                    id: SmartLabel.Focus,
                    itemCount: focusSummary.count,
                    categoryRepartition: focusSummary.categoryRepartition,
                    menuInfo: {
                        color: SmartLabelInfos[SmartLabel.Focus].color,
                        icon: "",
                        title: SmartLabel.Focus,
                    },
                    isFavorite: false,
                },
                {
                    id: SmartLabel.PourInfo,
                    itemCount: pourInfoSummary.count,
                    menuInfo: {
                        color: SmartLabelInfos[SmartLabel.PourInfo].color,
                        icon: "",
                        title: SmartLabel.PourInfo,
                    },
                    isFavorite: false,
                },
                {
                    id: SmartLabel.Newsletter,
                    itemCount: newsletterSummary.count,
                    menuInfo: {
                        color: SmartLabelInfos[SmartLabel.Newsletter].color,
                        icon: "",
                        title: SmartLabel.Newsletter,
                    },
                    isFavorite: false,
                },
                // {
                //   id: SmartLabel.Suspect,
                //   itemCount: suspectSummary.count,
                //   menuInfo: {
                //     color: SmartLabelInfos[SmartLabel.Suspect].color,
                //     icon: "",
                //     title: SmartLabel.Suspect
                //   },
                //   isFavorite: false
                // },
                {
                    id: SmartLabel.Autre,
                    itemCount: autreSummary.count,
                    menuInfo: {
                        color: SmartLabelInfos[SmartLabel.Autre].color,
                        icon: "",
                        title: SmartLabel.Autre,
                    },
                    isFavorite: false,
                },
                {
                    id: SmartLabel.Traites,
                    itemCount: traitesSummary.count,
                    menuInfo: {
                        color: SmartLabelInfos[SmartLabel.Traites].color,
                        icon: "",
                        title: SmartLabel.Traites,
                    },
                    isFavorite: false,
                },
            ];
        } catch (error) {
            logger.error(
                LOGS.MailManager,
                "Error fetching smart label nav bar content",
                error,
            );
        }

        logger.info(
            [LOGS.MailManager],
            "SmartLabelNavItems",
            smartLabelNavItems,
        );

        return smartLabelNavItems;
    }

    /**
     * Gets the count (and category repartition for Focus ) for a specific smart label -
     * Reads directly from the internal smartLabelEntries store (no DB/CustomView lookup)
     *
     * @param {SmartLabel} label - The smart label to count emails for
     * @returns {Promise<SmartLabelSummary>} The count and category repartition for this smart label
     */
    private async _getSmartLabelSummary(
        label: SmartLabel,
    ): Promise<SmartLabelSummary> {
        let categoryRepartition: CategoryRepartition[] = [];

        try {
            const ids =
                this.customViewManager._getSmartLabelInternetMessageIds(label);

            if (label === SmartLabel.Focus) {
                categoryRepartition =
                    await this._getCustomViewMailsCategoryRepartition(ids);
            }

            // ??? Need to filter with this._filterPastInvitationsForFocusView(focusMails, true) ???

            let summary: SmartLabelSummary = {
                categoryRepartition: categoryRepartition,
                count: ids.length,
            };

            return summary;
        } catch (error) {
            logger.error(
                LOGS.MailManager,
                `getSmartLabelSummary - Error getting count for SmartLabel ${label}`,
                error,
            );
            return {
                categoryRepartition: [],
                count: 0,
            };
        }
    }

    /**
     * Calculates the distribution by category of emails in a custom view.
     *
     * @param customViewId - Identifiant de la vue personnalisée à analyser
     * @returns Tableau des répartitions par catégorie avec leurs pourcentages, ou tableau vide en cas d'erreur
     */
    private async _getCustomViewMailsCategoryRepartition(
        internetMessageIds: string[],
    ): Promise<CategoryRepartition[]> {
        logger.info(
            [LOGS.MailManager],
            `log _getCustomViewMailsRepartition()...`,
        );

        let categoryRepartition: CategoryRepartition[] = [];

        const rawCustomViewMails = internetMessageIds
            .map((internetMessageId) =>
                this.mailSync._getCanonicalMail(internetMessageId),
            )
            .filter((mail): mail is Mail => mail != null);
        // La répartition par catégorie exclut les invitations passées (cohérence avec la vue Focus affichée)
        const customViewMails = this._filterPastInvitationsForFocusView(
            rawCustomViewMails,
            true,
        );

        try {
            const importantMails: Mail[] = customViewMails.filter(
                (mail: Mail) => {
                    if (mail.aiDetails) {
                        return mail.aiDetails.categories.includes(
                            Categories.Important,
                        );
                    }
                },
            );

            const rappelMails: Mail[] = customViewMails.filter((mail: Mail) => {
                if (mail.aiDetails) {
                    return mail.aiDetails.categories.includes(
                        Categories.Rappel,
                    );
                }
            });

            const reponse_attendueMails: Mail[] = customViewMails.filter(
                (mail: Mail) => {
                    if (mail.aiDetails) {
                        return mail.aiDetails.categories.includes(
                            Categories.ReponseAttendue,
                        );
                    }
                },
            );

            const informatifMails: Mail[] = customViewMails.filter(
                (mail: Mail) => {
                    if (mail.aiDetails) {
                        return mail.aiDetails.categories.includes(
                            Categories.Informatif,
                        );
                    }
                },
            );

            // const suspectMails: Mail[] = customViewMails.filter((mail: Mail) => {
            // if (mail.aiDetails) {
            //   return mail.aiDetails.categories.includes(Categories.Suspect);
            // }
            // });

            const autreMails: Mail[] = customViewMails.filter((mail: Mail) => {
                if (mail.aiDetails) {
                    return mail.aiDetails.categories.includes(Categories.Autre);
                }
            });

            // [#1672 — fix ④ SPEC-6/B-06/RC-NULL-CANONICAL] La base de la répartition DOIT être le
            // compteur Focus lui-même (= nombre d'ids du store), et NON le sous-ensemble des mails
            // résolus (`_getCanonicalMail` != null). Sinon, quand des mails Focus sont « en vol » (pas
            // encore dans le Record), la base < compteur → Σ segments ≠ count (répartition faussée,
            // INV-6). En prenant `internetMessageIds.length` comme dénominateur, les mails non encore
            // connus deviennent un « reste non réparti » toléré (SPEC-6) plutôt qu'une distorsion.
            let percentageTotalCount = internetMessageIds.length;

            let importantPercentage = 0;
            let rappelPercentage = 0;
            let reponse_attenduePercentage = 0;
            let informatifPercentage = 0;
            // let suspectPercentage           = 0;
            let autrePercentage = 0;

            if (percentageTotalCount !== 0) {
                // Calcul des pourcentages avec arrondi par défaut
                importantPercentage = Math.floor(
                    100 * (importantMails.length / percentageTotalCount),
                );
                rappelPercentage = Math.floor(
                    100 * (rappelMails.length / percentageTotalCount),
                );
                reponse_attenduePercentage = Math.floor(
                    100 * (reponse_attendueMails.length / percentageTotalCount),
                );
                informatifPercentage = Math.floor(
                    100 * (informatifMails.length / percentageTotalCount),
                );
                // suspectPercentage           = Math.floor(100 * (suspectInternetMessageIds.length / percentageTotalCount));
                autrePercentage = Math.floor(
                    100 * (autreMails.length / percentageTotalCount),
                );
            }

            categoryRepartition.push({
                title: Categories.Important,
                categoryColor: "" /** Obsolete => replaced by categoryId */,
                categoryId: Categories.Important,
                percentage: importantPercentage,
            });

            categoryRepartition.push({
                title: Categories.Rappel,
                categoryColor: "" /** Obsolete => replaced by categoryId */,
                categoryId: Categories.Rappel,
                percentage: rappelPercentage,
            });

            categoryRepartition.push({
                title: Categories.ReponseAttendue,
                categoryColor: "" /** Obsolete => replaced by categoryId */,
                categoryId: Categories.ReponseAttendue,
                percentage: reponse_attenduePercentage,
            });

            categoryRepartition.push({
                title: Categories.Informatif,
                categoryColor: "" /** Obsolete => replaced by categoryId */,
                categoryId: Categories.Informatif,
                percentage: informatifPercentage,
            });

            // categoryRepartition.push({
            //   title         : Categories.Suspect,
            //   categoryColor : '',  /** Obsolete => replaced by categoryId */
            //   categoryId    : Categories.Suspect,
            //   percentage    : suspectPercentage
            // })

            categoryRepartition.push({
                title: Categories.Autre,
                categoryColor: "" /** Obsolete => replaced by categoryId */,
                categoryId: Categories.Autre,
                percentage: autrePercentage,
            });

            logger.info(
                [LOGS.MailManager],
                `_getCustomViewMailsRepartition - log category repartition`,
                categoryRepartition,
            );

            return categoryRepartition;
        } catch (error) {
            logger.error(
                [LOGS.MailManager],
                `_getCustomViewMailsRepartition - Error getting repartition : ${error.message}`,
            );
            return [];
        }
    }

    /**
     * Retrieves the count of emails in a specific customView
     *
     * @returns {Promise<number>} The count of emails in the specified category
     */
    private async _getCustomViewNavBarContent(): Promise<NavItem[]> {
        // Ajoute les informations de favoris pour chaque élément de navigation
        const customViews = await this.customViewManager.getCustomViews();

        const sortedCustomViews = [...customViews].sort((a, b) => {
            // 1. D'abord, trier par statut de favori (les favoris en premier)
            if (a.isFavorite && !b.isFavorite) return -1;
            if (!a.isFavorite && b.isFavorite) return 1;

            // 2. Si même statut de favori, trier par displayOrder (si disponible)
            const aHasOrder =
                a.displayOrder !== null && a.displayOrder !== undefined;
            const bHasOrder =
                b.displayOrder !== null && b.displayOrder !== undefined;

            // Si les deux ont un displayOrder, les comparer
            if (aHasOrder && bHasOrder) {
                return a.displayOrder - b.displayOrder;
            }

            // Si seulement a a un displayOrder, le placer avant
            if (aHasOrder && !bHasOrder) return -1;

            // Si seulement b a un displayOrder, le placer avant
            if (!aHasOrder && bHasOrder) return 1;

            // 3. Si même statut de favori et pas de displayOrder, trier par nom
            return a.name.localeCompare(b.name);
        });

        const customViewNavItems: NavItem[] = sortedCustomViews.map(
            (customView) => ({
                id: customView.id!,
                itemCount: customView.internetMessageIds.length, // the count of mails in the custom view from Record
                menuInfo: {
                    color: "0xF0F0F0", // defaulted to
                    icon: customView.icon,
                    title: customView.name,
                },
                isFavorite: customView.isFavorite,
            }),
        );

        return customViewNavItems;
    }

    /**
     * @method _filterPastInvitationsForFocusView
     * Filtre les mails d'invitation dont l'événement est terminé, uniquement pour la vue Focus.
     * ─────────────────────────────────────────────────────────────────────────────
     * Règle d'activation : isFocusView doit être true (CustomViewType.Focus détecté
     *   via _getCustomViewType dans fetchCustomViewMails).
     * Règle de comparaison : on compare uniquement les dates (pas les heures),
     *   donc un événement qui se termine aujourd'hui reste visible.
     * ─────────────────────────────────────────────────────────────────────────────
     * @param mails        - Liste des mails à filtrer
     * @param isFocusView  - True si la vue courante est le SmartLabel Focus
     * @returns Tableau filtré (les mails non-invitation sont toujours conservés)
     */
    private _filterPastInvitationsForFocusView(
        mails: Mail[],
        isFocusView: boolean,
    ): Mail[] {
        if (!isFocusView) return mails;

        // Minuit aujourd'hui — comparaison sur la date uniquement (pas l'heure)
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const filtered = mails.filter((mail) => {
            // Les mails sans invitation passent toujours
            if (!mail.linkedEventDetails?.endDateTime) return true;

            const endDate = new Date(
                mail.linkedEventDetails.endDateTime as string,
            );
            endDate.setHours(0, 0, 0, 0);

            // Garder si la date de fin est aujourd'hui ou dans le futur
            if (endDate >= today) return true;

            // L'auto-unfocus est géré côté backend (Option B dans mail.service.impl.ts).
            // Ce filtre est purement d'affichage : les invitations passées sont masquées
            // de la vue Focus, quel que soit leur isFocus en mémoire.
            return false;
        });

        const removedCount = mails.length - filtered.length;
        if (removedCount > 0) {
            logger.info(
                LOGS.MailManager,
                `_filterPastInvitationsForFocusView - ${removedCount} invitation(s) passée(s) masquée(s)`,
            );
        }

        return filtered;
    }

    /* [WS-C / AB#1576] CODE MORT — commenté le 2026-06-07.
       * La résolution internetMessageId → mailId Cosmos n'est plus nécessaire côté SDK :
       * la suppression passe désormais par apiManager.deleteAnalyzedMailsByInternetMessageIds()
       * (le cosmosId reste interne au repository Api-Backend). À supprimer si aucune régression.
      private async _getMailIdByInternetMessageId(internetMessageId: string, askedBy : string = 'unknown', ): Promise<string> {
        if (!this.dbManager) {
          throw new Error('DbManager is not set');
        }

        const mailIdsFromDb = await this.dbManager.getMailIdByInternetMessageIds([internetMessageId]);

        if (mailIdsFromDb && mailIdsFromDb.length == 0) {
          throw new Error(`_getMailIdByInternetMessageId asked by ${askedBy} - internetMessageId is not found in DB : ${internetMessageId}`);
        }

        return mailIdsFromDb[0];
      }
      */
}
